import { notyf } from '@modules';
import { pubsub } from '@services';

class ProductVariantSelect extends HTMLElement {
  abortController;

  connectedCallback() {
    this.initOptions();
  }

  /**
   * Initialize the options.
   */
  initOptions() {
    this.options = this.querySelectorAll('[data-variant-id]');
    this.url = this.dataset.url;
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.qsProductId;

    this.addEventListener('change', (event) => {
      const target = event.target.closest('[data-variant-id]');
      if (target && !this.abortController) this.onVariantChange({ target });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target.closest('[data-variant-id]');
      if (target && !this.abortController) {
        event.preventDefault();
        target.checked = true;
        this.onVariantChange({ target });
      }
    });
  }

  /**
   * Handle variant change.
   * @param {Event} event - The event object.
   */
  async onVariantChange({ target }) {
    if (this.abortController) return;
    this.abortController = new AbortController();

    const { variantId } = target.dataset;
    const params = this.createParams(variantId);

    // Immediately uncheck all other checkboxes within the same fieldset to improve perceived speed.
    this.updateFieldset(target);

    try {
      const fetchUrl = `${this.url}?${params}`;
      const response = await fetch(fetchUrl, {
        signal: this.abortController.signal,
      });

      const text = await response.text();
      const parsedHTML = new DOMParser().parseFromString(text, 'text/html');

      pubsub.publish(`variant:changed:${this.productId}`, {
        variantId,
        html: parsedHTML,
      });

      this.initOptions();

      if (this.sectionId === 'fetch-quick-shop') return;

      this.updateURL(variantId);
    }
    catch (error) {
      if (error.name === 'AbortError')
        return console.error('Variant change request was aborted due to a new variant selection.');

      console.error(error.message);
      notyf.error(window.toast_translations.product.variant_error);
    }
    finally {
      this.abortController = null;
      document.querySelector(`#${target.id}`).focus();
    }
  }

  /**
   * Update URL
   * @param {String} value - URL value
   */
  updateURL(value) {
    const { searchParams } = new URL(window.location);
    searchParams.set('variant', value);

    // Replace the current history state with the new search params, without reloading the page
    history.replaceState(null, '', `${window.location.pathname}?${searchParams}`);
  }

  /**
   * Create the params.
   * @param {String} variantId - The variant ID.
   * @returns {String} The params.
   */
  createParams(variantId) {
    return [`section_id=${this.sectionId}`, `variant=${variantId}`].join('&');
  }

  /**
   * Update the fieldset.
   * @param {HTMLElement} target - The target element.
   */
  updateFieldset(target) {
    const fieldset = target.closest('fieldset');
    if (!fieldset) return;

    fieldset.querySelectorAll('[data-variant-id]').forEach((option) => {
      option.checked = option === target;
    });
  }
}

customElements.define('product-variant-select', ProductVariantSelect);
